"use client";

import { IconCheckOutline18, IconChevronExpandYOutline18, IconWorkflowOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getQueryClient } from "@/lib/queryClient";
import type { Workflow as WorkflowType, WorkflowInput } from "@/stores/workflowsStore";
import { fetchWorkflowById } from "../../_hooks/workflowsApi";
import { useWorkflow } from "../../_hooks/useWorkflow";
import { useWorkflowsPage } from "../../_hooks/useWorkflowsQuery";
import { workflowsKeys } from "../../_hooks/workflowsQueryKeys";
import { useEffect, useMemo, useState } from "react";
interface WorkflowSelectorProps {
  label?: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  /** Callback to set other fields on the same node (e.g., scaffold payload from inputSchema). */
  onFieldChange?: (name: string, value: unknown) => void;
}

// Trigger type → short display label
const TRIGGER_LABELS: Record<string, string> = {
  manual: "Manual",
  webhook: "Webhook",
  cronjob: "Cron",
  schedule: "Schedule",
  "evm-log": "EVM Log",
  "token-price-cexes": "Price",
  "erc20-receive": "ERC20 Receive",
  "erc20-send": "ERC20 Send",
  "trigger-workflow": "Sub-workflow"
};

/** Build a default value for an inputSchema type. */
function defaultForType(type: WorkflowInput["type"]): unknown {
  switch (type) {
    case "number":
      return 0;
    case "boolean":
      return false;
    case "object":
      return {};
    case "array":
      return [];
    default:
      return "";
  }
}

/** Build a payload scaffold from a workflow's inputSchema. */
function buildPayloadScaffold(inputs: WorkflowInput[]): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const input of inputs) {
    if (input.key) {
      payload[input.key] = defaultForType(input.type);
    }
  }
  return payload;
}
export default function WorkflowSelector({
  label,
  description,
  value,
  onChange,
  onFieldChange
}: WorkflowSelectorProps) {
  const [open, setOpen] = useState(false);
  // Local controlled input value updates immediately for snappy typing;
  // the debounced `searchQuery` is what actually drives the API call so
  // we don't fire one request per keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => setSearchQuery(searchInput), 250);
    return () => window.clearTimeout(id);
  }, [searchInput]);
  const {
    selectedOrgId
  } = useOrganizations();

  // Paginated active workflow list backs the dropdown. Backend-side filtering
  // via `q` + `status` keeps bandwidth bounded even on large orgs — we don't
  // pull every workflow row just to render a picker.
  // TODO: switch to infinite-scroll if usage shows users regularly hitting the
  // 100-row cap.
  const listQuery = useWorkflowsPage({
    page: 0,
    pageSize: 100,
    status: "active",
    sort: "name:asc",
    q: searchQuery || undefined
  }, {
    enabled: !!selectedOrgId
  });
  const workflows = listQuery.data?.items ?? [];
  // Only show the "Loading workflows..." placeholder on the INITIAL load.
  // Subsequent fetches (each search keystroke settling) keep showing the
  // previous data via keepPreviousData instead of blinking to empty.
  const loading = listQuery.isLoading && !listQuery.data;

  // Selected workflow may not be in the visible list (filtered out by search
  // or status). Fetch it directly so we can render its name + scaffold its
  // payload on selection.
  const selectedQuery = useWorkflow(value || null);

  // Server returns rows already sorted by name:asc (see useWorkflowsPage
  // call above). Trust the contract instead of re-sorting on every render.
  const filteredWorkflows = workflows;
  const selectedWorkflow: WorkflowType | null = useMemo(() => {
    if (!value) return null;
    return workflows.find(w => w.id === value) ?? selectedQuery.data ?? null;
  }, [value, workflows, selectedQuery.data]);
  const handleSelect = async (workflowId: string) => {
    onChange(workflowId);
    setOpen(false);
    // Reset search so the next open shows the full list, and the debounced
    // query doesn't keep re-filtering by a stale term after selection.
    setSearchInput("");

    // Scaffold payload from the target workflow's inputSchema. List items are
    // slim (no `definition`), so fetch-or-read-from-cache the full detail via
    // the query client. This shares cache with `useWorkflow` above.
    if (onFieldChange) {
      let target: WorkflowType | null = workflows.find(w_0 => w_0.id === workflowId) ?? selectedQuery.data ?? null;
      if (!target?.definition) {
        try {
          target = await getQueryClient().fetchQuery({
            queryKey: workflowsKeys.detail(workflowId),
            queryFn: () => fetchWorkflowById(workflowId)
          });
        } catch {
          target = null;
        }
      }
      const schema = target?.definition?.inputSchema;
      if (schema && schema.length > 0) {
        onFieldChange("payload", buildPayloadScaffold(schema));
      } else {
        // Clear payload when switching to a workflow with no inputSchema
        onFieldChange("payload", {});
      }
    }
  };
  return <div className="space-y-2">
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {selectedWorkflow ? <span className="flex min-w-0 items-center gap-2">
                <IconWorkflowOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" />
                <span className="truncate">{selectedWorkflow.name}</span>
                <span className="text-3xs flex-shrink-0 rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Active
                </span>
              </span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a workflow...</span>}
            <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search workflows..." value={searchInput} onValueChange={setSearchInput} />
            <CommandList>
              {loading ? <div className="body-sm py-6 text-center">Loading workflows...</div> : <>
                  <CommandEmpty>No workflows found.</CommandEmpty>
                  <CommandGroup>
                    {filteredWorkflows.map(w_1 => {
                  // inputCount pre-computed into list_metadata; falls
                  // back to definition parse if a detail-loaded row is
                  // passed through (shouldn't happen on this path).
                  const inputs = w_1.inputCount ?? w_1.definition?.inputSchema?.length ?? 0;
                  return <CommandItem key={w_1.id} value={w_1.id} onSelect={() => handleSelect(w_1.id)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 cursor-pointer">
                          <div className="flex w-full items-start gap-2 py-0.5">
                            <IconCheckOutline18 className={cn("mt-0.5 h-4 w-4 flex-shrink-0", value === w_1.id ? "opacity-100" : "opacity-0")} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-ds-text-primary truncate font-medium">{w_1.name}</span>
                                {TRIGGER_LABELS[w_1.type] && <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled text-3xs flex-shrink-0 rounded px-1.5 py-0.5 font-medium">
                                    {TRIGGER_LABELS[w_1.type]}
                                  </span>}
                                {inputs > 0 && <span className="text-3xs flex-shrink-0 rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                    {inputs} input{inputs !== 1 ? "s" : ""}
                                  </span>}
                              </div>
                              {w_1.description && <p className="label-xs mt-0.5 truncate">{w_1.description}</p>}
                              <span className="text-ds-text-disabled dark:text-ds-text-muted text-3xs mt-0.5 block font-mono">
                                {w_1.id}
                              </span>
                            </div>
                          </div>
                        </CommandItem>;
                })}
                  </CommandGroup>
                </>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>;
}